import {PromptTarget} from '../state/useNovaPromptState';
import {NovaVariable} from './novaVariableLibrary';
import {NovaMarkdownWarning, scanMarkdown} from './novaMarkdownScan';

export const NovaIssueKind = {
	// The reference names a variable that existed and has since been removed.
	BrokenReference: 'BROKEN_REFERENCE',
	// The reference names something that was never a variable.
	UnknownVariable: 'UNKNOWN_VARIABLE',
	// The Markdown around the references is structurally broken.
	Markdown: 'MARKDOWN',
	// A section no run can be built without is empty. The only issue that blocks saving.
	EmptyRequired: 'EMPTY_REQUIRED',
	// Customer data typed into the System prompt or agent.md, which only the User prompt may carry.
	CustomerData: 'CUSTOMER_DATA',
} as const;
export type NovaIssueKind = (typeof NovaIssueKind)[keyof typeof NovaIssueKind];

export interface NovaInputIssue {
	kind: NovaIssueKind;
	// The reference the issue is about, braces included. Absent on a Markdown warning.
	token?: string;
	// Which Markdown rule fired. Absent on a reference issue.
	warning?: NovaMarkdownWarning;
}

export interface NovaValidationContext {
	// The variables the library offers for insertion.
	offered: NovaVariable[];
	// The variables that have been removed, served separately so a reference to one reads as broken
	// rather than as a name that never existed.
	removed: NovaVariable[];
	// Tokens that may only enter the User prompt. Set for a workflow, from its declared customer data
	// and its files; absent for a task agent, whose variables may go in any prompt.
	customerDataTokens?: ReadonlySet<string>;
}

const FILE_TOKEN_PREFIX = '{file:';

// The sections the service cannot ground a run without. agent.md is optional, so it is not one.
const REQUIRED_TARGETS: PromptTarget[] = [PromptTarget.System, PromptTarget.User];

function isBlank(text: string): boolean {
	return text.trim() === '';
}

// Matches the service's own rule (agentic-workflow `novaRequiredInputs.ts`) exactly: both sections
// non-empty after trimming. The service refuses to save, restore or deploy anything else, so this
// is what gates Save and the Deploy tab's add controls rather than a second definition that can drift.
export function isRunnable(inputs: {systemPrompt: string; userPromptTemplate: string}): boolean {
	return !isBlank(inputs.systemPrompt) && !isBlank(inputs.userPromptTemplate);
}

// Classifies one reference against the library, or returns null when there is nothing to say about
// it. Order matters: a removed variable reads as broken because "this no longer exists" is the most
// actionable thing that can be said about it.
//
// A reference the library does offer can still be in the wrong place: customer data in the System
// prompt or agent.md is reported, though never removed — the run drops it.
export function classifyToken(token: string, target: PromptTarget, context: NovaValidationContext): NovaInputIssue | null {
	if (context.removed.some(variable => variable.token === token)) {
		return {kind: NovaIssueKind.BrokenReference, token};
	}
	const offered = context.offered.find(variable => variable.token === token);
	if (!offered) {
		// A file reference the library no longer offers points at a detached attachment — something
		// that did exist, so broken rather than unknown.
		return token.startsWith(FILE_TOKEN_PREFIX)
			? {kind: NovaIssueKind.BrokenReference, token}
			: {kind: NovaIssueKind.UnknownVariable, token};
	}
	if (target !== PromptTarget.User && context.customerDataTokens?.has(token)) {
		return {kind: NovaIssueKind.CustomerData, token};
	}
	return null;
}

// Everything worth telling the author about one authored input: bad references first, then Markdown
// warnings. Those are advisory — they do not block saving, and nothing here changes the text. A blank
// System prompt or User prompt template is the exception: it is reported as required, and Save is
// blocked on it (see `isRunnable`). A blank agent.md is silent, since agent.md is optional.
//
// A reference is reported once however often it appears, so an input that mentions the same removed
// variable five times gets one line about it rather than five.
export function validateInput(text: string, target: PromptTarget, context: NovaValidationContext): NovaInputIssue[] {
	if (isBlank(text)) {
		return REQUIRED_TARGETS.includes(target) ? [{kind: NovaIssueKind.EmptyRequired}] : [];
	}
	const scan = scanMarkdown(text);
	const issues: NovaInputIssue[] = [];
	const reported = new Set<string>();
	for (const occurrence of scan.tokens) {
		if (reported.has(occurrence.token)) {
			continue;
		}
		reported.add(occurrence.token);
		const issue = classifyToken(occurrence.token, target, context);
		if (issue) {
			issues.push(issue);
		}
	}
	for (const warning of scan.warnings) {
		issues.push({kind: NovaIssueKind.Markdown, warning});
	}
	return issues;
}
